package one.healthium.paywall.ui

import androidx.lifecycle.viewModelScope
import co.touchlab.kermit.Logger
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import one.healthium.base.launcher.PlatformLauncher
import one.healthium.base.preferences.AppPreferences
import one.healthium.domain.app_visit.AppVisitCounter
import one.healthium.domain.common.PlatformValue
import one.healthium.domain.paywall.PaywallInteractor
import one.healthium.domain.paywall.models.Paywall
import one.healthium.domain.paywall.models.PaywallMainPlansView
import one.healthium.domain.paywall.models.PaywallOtherPlansView
import one.healthium.domain.user.UserInteractor
import one.healthium.domain.user.models.SubscriptionStatus
import one.healthium.payments.BillingManager
import one.healthium.payments.models.ProductDetails
import one.healthium.payments.models.ProductOptions
import one.healthium.payments.models.ProductType
import one.healthium.ui.common.event_producer.EventProducer
import one.healthium.ui.common.message.UiMessage
import one.healthium.ui.common.message.UiMessageManager
import one.healthium.ui.common.vm.BaseViewModel
import one.primehealth.analytics.AppAnalytics
import one.primehealth.analytics.AppAnalyticsPlatform
import one.primehealth.analytics.events.Events

class PaywallViewModel(
    private val paywallInteractor: PaywallInteractor,
    private val userInteractor: UserInteractor,
    private val billingManager: BillingManager,
    private val appVisitCounter: AppVisitCounter,
    private val appAnalyticsPlatform: AppAnalyticsPlatform,
    private val appAnalytics: AppAnalytics,
    appPreferences: AppPreferences,
) : BaseViewModel() {
    private val _state = MutableStateFlow(PaywallUiState())

    val state: StateFlow<PaywallUiState> = _state
    val event = EventProducer<PaywallEvent>()
    val uiMessages = UiMessageManager()

    private val paywallId = MutableStateFlow<String?>(null)

    init {
        combine(
            paywallId,
            appPreferences.remarketingActiveFlow
        ) { paywallId, remarketingActive ->
            loadPaywall(paywallId, remarketingActive)
        }.launch()

        userInteractor.observeUser()
            .filter { it.purchases.subscription == SubscriptionStatus.Active }
            .onEach {
                appAnalyticsPlatform.updateUser(it)
                event.trigger(PaywallEvent.PaywallPassed)
            }
            .catch { e ->
                Logger.e("PaywallViewModel observeUser error", e)
            }
            .launch()
    }

    suspend fun setPaywallId(paywallId: String?) {
        this.paywallId.emit(paywallId)
    }

    fun onPurchaseButtonClicked(platformLauncher: PlatformLauncher) {
        val options = _state.value.purchaseOptions
        if (options == null) {
            Logger.d("purchase options is null, ${_state.value}")
            return
        }

        viewModelScope.launch {
            try {
                _state.update { state -> state.copy(paymentInProgress = true) }
                appAnalytics.logEvent(
                    Events.InitiateCheckout(
                        productId = options.product.id,
                        price = options.product.price(null),
                        isRemarket = _state.value.paywall?.isRemarketing == true,
                    )
                )
                billingManager.launchPurchaseFlow(platformLauncher, options)
                userInteractor.syncLocalToRemoteUser()
                appAnalytics.logEvent(
                    Events.PurchaseCompleted(
                        productId = options.product.id,
                        price = options.product.price(null),
                        isRemarket = _state.value.paywall?.isRemarketing == true,
                    )
                )
                appVisitCounter.reset()
                // now wait for the user update
            } catch (e: Exception) {
                if (e !is CancellationException) {
                    Logger.e("launch purchase flow error ${e.message}", e)
                    if (e.message.isNullOrBlank().not()) {
                        uiMessages.add(UiMessage.throwable(e))
                    }
                }
                _state.update { state -> state.copy(paymentInProgress = false) }
            }
        }
    }

    fun onProductOfferClicked(platformLauncher: PlatformLauncher, productId: String) {
        _state.update { state -> state.copy(selectedProductId = productId) }
        onPurchaseButtonClicked(platformLauncher)
    }

    fun onRestoreClicked() {
        restorePurchase()
    }

    fun onSkipClicked() {
        launch {
            try {
                _state.update { state -> state.copy(paymentInProgress = true) }
                userInteractor.syncLocalToRemoteUser()
            } catch (e: Exception) {
                Logger.e("skip paywall error", e)
            } finally {
                _state.update { state -> state.copy(paymentInProgress = false) }
                event.trigger(PaywallEvent.PaywallPassed)
            }
        }
    }

    private suspend fun loadPaywall(paywallId: String?, remarketingActive: Boolean) {
        Logger.d("loadPaywall: paywallId=$paywallId, remarketingActive=$remarketingActive")
        _state.update { state -> state.copy(loading = true, paywall = null) }
        try {
            val paywallSet = paywallInteractor.getPaywallSet()
            val resolvedPaywallId = when {
                paywallId != null -> paywallId
                remarketingActive && paywallSet?.config?.remarketingId != null -> paywallSet.config.remarketingId
                else -> paywallSet?.config?.paywallId
            }
            Logger.d("loadPaywall: resolvedPaywallId=$resolvedPaywallId")
            if (resolvedPaywallId != null) {
                val paywall = paywallInteractor.getPaywall(resolvedPaywallId)
                if (paywall != null) {
                    val productDetails = try {
                        loadProducts(paywall)
                    } catch (e: Exception) {
                        Logger.e("PaywallViewModel can not fetch products", e)
                        uiMessages.add(UiMessage.throwable(e))
                        emptyList<ProductDetails>()
                    }
                    _state.update { state ->
                        state.copy(
                            loading = false,
                            paywallSet = paywallSet,
                            paywall = paywall,
                            productDetails = productDetails,
                            selectedProductId = paywall.productDefaults?.highlightedProduct,
                        )
                    }
                } else {
                    _state.update { state -> state.copy(loading = false) }
                    uiMessages.add(UiMessage.throwable(Exception("Paywall not found")))
                }
            } else {
                _state.update { state -> state.copy(loading = false) }
                uiMessages.add(UiMessage.throwable(Exception("Paywall not found")))
            }
        } catch (e: Exception) {
            Logger.e("load paywall error ${e.message}", e)
            _state.update { state -> state.copy(loading = false) }
            uiMessages.add(UiMessage.throwable(e))
        }
    }

    private suspend fun loadProducts(model: Paywall?): List<ProductDetails>? {
        if (model == null) return null

        val products = model.extractProducts()
        val productOptions = products.map {
            ProductOptions(it, ProductType.Subs) // TODO define product type
        }.distinct()
        return billingManager.getProducts(productOptions)
    }

    private fun restorePurchase() {
        viewModelScope.launch {
            try {
                _state.update { state -> state.copy(paymentInProgress = true) }
                billingManager.launchRestoreFlow()
                userInteractor.syncLocalToRemoteUser()
                appVisitCounter.reset()
                // now wait for the user update
            } catch (e: Exception) {
                Logger.e("launch purchase flow error ${e.message}", e)
                if (e !is CancellationException && e.message.isNullOrBlank().not()) {
                    uiMessages.add(UiMessage.throwable(e))
                }
                _state.update { state -> state.copy(paymentInProgress = false) }
            }
        }
    }
}

private fun Paywall.extractProducts(): List<String> {
    return PlatformValue(
        android = { extractProductsAndroid() },
        ios = { extractProductsIOS() },
        web = { emptyList() }
    ).value()
}

private fun Paywall.extractProductsIOS(): List<String> {
    return views.filter { it is PaywallMainPlansView || it is PaywallOtherPlansView }
        .flatMap {
            when (it) {
                is PaywallMainPlansView -> it.config.controls.map { control -> control.product }
                is PaywallOtherPlansView -> it.config.plans.map { plan -> plan.product }
                else -> emptyList()
            }
        }
}

private fun Paywall.extractProductsAndroid(): List<String> {
    return if (productAndroid != null) listOf(productAndroid!!) else emptyList()
}